import { differenceInMinutes } from "date-fns";

const FAMA_PREFIX = "fama_";
const MAX_CACHE_TIME_MIN = 60 * 24; // 1 day

export type CacheValue = {
  cachedObject: any;
  lastUpdated: Date;
  clearOnRefresh: boolean;
};

function setPersistentCacheItem<T>(
  key: string,
  value: T,
  clearOnRefresh: boolean
) {
  const cacheItemValue: CacheValue = {
    cachedObject: value,
    lastUpdated: new Date(),
    clearOnRefresh,
  };
  localStorage.setItem(key, JSON.stringify(cacheItemValue));
}

function getPersistentCacheItem(key: string): CacheValue | undefined {
  const rawItem = localStorage.getItem(key);
  if (!rawItem) return;

  const parsedItem: CacheValue = JSON.parse(rawItem);

  if (!parsedItem) return;

  const difference = differenceInMinutes(
    new Date(parsedItem.lastUpdated),
    new Date()
  );

  if (difference >= MAX_CACHE_TIME_MIN) return;
  return parsedItem;
}

function loadLocalStorageCachedValues(): Map<string, CacheValue> {
  const cachedItems = new Map<string, CacheValue>();
  Object.keys(localStorage).forEach((key) => {
    if (key.includes(FAMA_PREFIX)) {
      try {
        const cachedValue: CacheValue | undefined = getPersistentCacheItem(key);
        if (cachedValue?.clearOnRefresh) {
          localStorage.removeItem(key);
          return;
        }
        if (cachedValue) {
          cachedItems.set(key, cachedValue);
        }
      } catch (error) {
        console.error(error);
        localStorage.removeItem(key);
      }
    }
  });

  return cachedItems;
}

function clearLocalStorageCachedValues() {
  Object.keys(localStorage).forEach((key) => {
    if (key.includes(FAMA_PREFIX)) {
      localStorage.removeItem(key);
    }
  });
}

export function getOrCreatePersistentCacheItem(
  key: string,
  value?: any,
  clearOnRefresh?: boolean
) {
  const mkey = `${FAMA_PREFIX}${key}`;
  if (!value) return getPersistentCacheItem(mkey)?.cachedObject;
  setPersistentCacheItem(mkey, value, !!clearOnRefresh);
  return value;
}

export function useStorageMap() {
  function updateCacheRefKey<T>(
    key: string,
    value: T,
    clearOnRefresh: boolean
  ) {
    const mkey = `${FAMA_PREFIX}${key}`;
    setPersistentCacheItem(mkey, value, clearOnRefresh);
  }

  function clearCacheRefKey(key: string) {
    const mkey = `${FAMA_PREFIX}${key}`;
    localStorage.removeItem(mkey);
  }

  function buildCacheInfo() {
    const cacheInfo: { [key: string]: any } = {};
    loadLocalStorageCachedValues().forEach((value, key) => {
      cacheInfo[key.replace("fama_", "")] = value.cachedObject;
    });
    return cacheInfo;
  }

  return {
    buildCacheInfo,
    updateCacheRefKey,
    clearCacheRefKey,
    clearLocalStorageCachedValues,
  };
}
